/**
 * ApiProvider — the dependency-injection seam between shared code and each app.
 *
 * Each app builds a concrete `TrinetraApi` (its own axios instance, auth, node-scoping,
 * tunnel-unwrapping) and passes it here at the root. Shared pages call `useApi()` and
 * receive whichever implementation the host app provided — they never know which node
 * they run on.
 *
 *   // app root (cloud or local)
 *   <ApiProvider api={createCloudApi()}>
 * 
 *     <App />
 *   </ApiProvider>
 *
 *   // any shared page/component
 *   const { roles } = useApi();
 *   const data = await roles.list();
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { TrinetraApi } from './contracts';

const ApiContext = createContext<TrinetraApi | null>(null);

export function ApiProvider({ api, children }: { api: TrinetraApi; children: ReactNode }) {
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

/** Access the injected API. Throws if used outside <ApiProvider> (a wiring bug). */
export function useApi(): TrinetraApi {
  const api = useContext(ApiContext);
  if (!api) {
    throw new Error('useApi() must be used within an <ApiProvider>. Did the app root forget to mount it?');
  }
  return api;
}
